import type { CatalogEvent } from "@/features/catalog/mediators/catalogEvents";
import { createMediator, type Mediator } from "@/shared/lib/componentHierarchy";

export type CatalogContextMenuEvent = Extract<
  CatalogEvent,
  {
    type:
      | "catalog/context-delete-requested"
      | "catalog/context-hide-requested"
      | "catalog/context-show-requested"
      | "catalog/context-ng-title-requested"
      | "catalog/context-ng-image-requested"
      | "catalog/context-history-remove-requested"
      | "catalog/context-close-requested";
  }
>;

export type CatalogContextMenuEffect =
  | { readonly type: "delete" }
  | { readonly type: "hide" }
  | { readonly type: "show" }
  | { readonly type: "ng-title" }
  | { readonly type: "ng-image" }
  | { readonly type: "remove-history" }
  | { readonly type: "close" };

export function isCatalogContextMenuEvent(
  event: CatalogEvent,
): event is CatalogContextMenuEvent {
  return event.type.startsWith("catalog/context-");
}

export function createCatalogContextMenuMediator(
  runEffect: (effect: CatalogContextMenuEffect) => void,
): Mediator<
  Record<string, never>,
  CatalogContextMenuEvent,
  CatalogContextMenuEffect
> {
  return createMediator<
    Record<string, never>,
    CatalogContextMenuEvent,
    CatalogContextMenuEffect
  >(
    {},
    (state, event) => {
      const action: CatalogContextMenuEffect | null = (() => {
        switch (event.type) {
          case "catalog/context-delete-requested":
            return { type: "delete" };
          case "catalog/context-hide-requested":
            return { type: "hide" };
          case "catalog/context-show-requested":
            return { type: "show" };
          case "catalog/context-ng-title-requested":
            return { type: "ng-title" };
          case "catalog/context-ng-image-requested":
            return { type: "ng-image" };
          case "catalog/context-history-remove-requested":
            return { type: "remove-history" };
          case "catalog/context-close-requested":
            return null;
        }
      })();
      return {
        status: "handled",
        state,
        effects: action ? [action, { type: "close" }] : [{ type: "close" }],
      };
    },
    { runEffect },
  );
}
